import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Save, RotateCcw, Undo2, Redo2, Lock, Unlock, Trash2, Plus,
  GripVertical, ChevronUp, ChevronDown, Eye, EyeOff, Monitor,
  Smartphone, Tablet, Image as ImageIcon, Type, Menu, MousePointer,
  Layout, RefreshCcw, X, Spacing, AlignJustify,
} from "lucide-react";
import { mergeSaveCmsField } from "@/lib/api";
import { useCms } from "@/lib/cms";
import { Logo } from "@/components/Brand";
import { ImageUpload } from "./Content";
import { FONT_GROUPS, fontCss } from "./CertificateDesigner";

// ── Shared primitives ─────────────────────────────────────────────────────────
const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white w-full";
const Select = ({ value, onChange, children, className = "" }) => (
  <select value={value} onChange={onChange} className={`${SI} ${className}`}>{children}</select>
);
const NumInput = ({ value, onChange, min, max, step = 1, placeholder }) => (
  <input type="number" value={value ?? ""} min={min} max={max} step={step} placeholder={placeholder}
    onChange={onChange} className={SI} />
);
const TextInput = ({ value, onChange, placeholder, className = "" }) => (
  <input type="text" value={value ?? ""} placeholder={placeholder}
    onChange={onChange} className={`${SI} ${className}`} />
);
const Lbl = ({ children }) => (
  <span className="text-xs font-medium text-gray-500 block mb-1">{children}</span>
);
const Row = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-${cols} gap-3`}>{children}</div>
);
const Field = ({ label, children }) => (
  <label className="block"><Lbl>{label}</Lbl>{children}</label>
);
const Section = ({ title, children }) => (
  <div className="mb-6">
    <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3 pb-1 border-b border-gray-100">{title}</div>
    <div className="grid gap-3">{children}</div>
  </div>
);
const ColorField = ({ label, value, onChange }) => (
  <label className="block">
    <Lbl>{label}</Lbl>
    <input type="color" value={value || "#000000"} onChange={onChange}
      className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
  </label>
);
const NudgeBtn = ({ onClick, children }) => (
  <button type="button" onClick={onClick}
    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 text-xs font-bold">
    {children}
  </button>
);
const SliderInput = ({ label, value, onChange, min = 0, max = 200, step = 1, unit = "px" }) => (
  <label className="block">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className="text-xs font-mono text-gray-400">{value ?? 0}{unit}</span>
    </div>
    <div className="flex items-center gap-2">
      <input type="range" min={min} max={max} step={step} value={value ?? min}
        onChange={onChange} className="flex-1 h-1.5 accent-[#4F46E5] cursor-pointer" />
      <input type="number" min={min} max={max} step={step} value={value ?? ""}
        onChange={onChange} className="w-16 px-2 py-1.5 rounded-lg border border-gray-200 text-xs outline-none text-center" />
    </div>
  </label>
);

// Spacing box: 4 independent values (top/right/bottom/left)
function SpacingBox({ label, prefix, d, patch, max = 80 }) {
  const s = (side) => `${prefix}_${side}`;
  const v = (side) => d[s(side)] ?? 0;
  const set = (side, val) => patch({ [s(side)]: Number(val) });
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="grid grid-cols-2 gap-2">
        {[["top", "Top"], ["right", "Right"], ["bottom", "Bottom"], ["left", "Left"]].map(([side, lbl]) => (
          <label key={side} className="block">
            <span className="text-[10px] text-gray-400 block mb-0.5">{lbl}</span>
            <div className="flex items-center gap-1">
              <input type="range" min={0} max={max} value={v(side)}
                onChange={(e) => set(side, e.target.value)}
                className="flex-1 h-1.5 accent-[#4F46E5] cursor-pointer" />
              <input type="number" min={0} max={max} value={v(side)}
                onChange={(e) => set(side, e.target.value)}
                className="w-12 px-1.5 py-1 rounded-md border border-gray-200 text-xs text-center outline-none" />
            </div>
          </label>
        ))}
      </div>
      <button type="button"
        onClick={() => patch({ [s("top")]: 0, [s("right")]: 0, [s("bottom")]: 0, [s("left")]: 0 })}
        className="mt-1 text-[10px] text-[#4F46E5] hover:underline">Reset {label.toLowerCase()}</button>
    </div>
  );
}

// ── Default nav items ─────────────────────────────────────────────────────────
const DEFAULT_NAV_ITEMS = [
  { id: "n1", label: "Programs",           to: "/programs", visible: true, device: "all", target: "_self" },
  { id: "n2", label: "Gallery",            to: "/gallery",  visible: true, device: "all", target: "_self" },
  { id: "n3", label: "Podcast",            to: "/podcast",  visible: true, device: "all", target: "_self" },
  { id: "n4", label: "Skilled Teachers",   to: "/experts",  visible: true, device: "all", target: "_self" },
  { id: "n5", label: "About",              to: "/about",    visible: true, device: "all", target: "_self" },
  { id: "n6", label: "Verify Certificate", to: "/verify",   visible: true, device: "all", target: "_self" },
  { id: "n7", label: "Contact",            to: "/contact",  visible: true, device: "all", target: "_self" },
];

// ── Tabs (Desktop only) ───────────────────────────────────────────────────────
const TABS = [
  { id: "logo",     label: "Logo",         icon: ImageIcon },
  { id: "name",     label: "Company Name", icon: Type },
  { id: "nav",      label: "Navigation",   icon: Menu },
  { id: "buttons",  label: "Buttons",      icon: MousePointer },
  { id: "spacing",  label: "Spacing",      icon: Layout },
  { id: "tablet",   label: "Tablet",       icon: Tablet },
];

const BREAKPOINTS = [480, 640, 768, 992, 1024];

// ── Default reset values ──────────────────────────────────────────────────────
const DEFAULTS = {
  logo_url: "", logo_width: 32, logo_height: 32, logo_radius: 8,
  logo_left_spacing: 0, logo_top_offset: 0, logo_bottom_offset: 0,
  logo_margin_right: 8, logo_offset_x: 0, logo_offset_y: 0, logo_scale: 1, logo_name_gap: 8,
  // logo spacing
  logo_margin_top: 0, logo_margin_bottom: 0, logo_padding_top: 0, logo_padding_right: 0,
  logo_padding_bottom: 0, logo_padding_left: 0,
  // company name
  company_name_font_size: 18, company_name_font_weight: 600,
  company_name_letter_spacing: -0.01, company_name_color: "#0F172A",
  company_name_valign: "center", company_name_offset_x: 0, company_name_offset_y: 0,
  company_name_margin_left: 0, company_name_margin_right: 0,
  company_name_font_family: "Inter", company_name_text_align: "left",
  company_name_text_transform: "none", company_name_opacity: 1, company_name_max_width: null,
  company_name_margin_top: 0, company_name_margin_bottom: 0,
  company_name_padding_top: 0, company_name_padding_right: 0,
  company_name_padding_bottom: 0, company_name_padding_left: 0,
  // nav
  nav_font_size: 14, nav_font_weight: 500, nav_letter_spacing: 0, nav_gap: 4,
  nav_text_color: "#4B5563", nav_hover_color: "#0F172A", nav_active_color: "#0F172A",
  nav_font_family: "Inter", nav_items: DEFAULT_NAV_ITEMS,
  nav_margin_top: 0, nav_margin_right: 0, nav_margin_bottom: 0, nav_margin_left: 0,
  nav_item_padding_top: 8, nav_item_padding_right: 12, nav_item_padding_bottom: 8, nav_item_padding_left: 12,
  nav_item_border_radius: 8,
  // buttons
  hide_login: false,
  signin_btn_text: "Sign in", signin_btn_bg: "transparent", signin_btn_color: "#374151",
  signin_btn_font_size: 14, signin_btn_font_weight: 500, signin_btn_border_radius: 8,
  signin_btn_padding_x: 12, signin_btn_padding_y: 8,
  signin_btn_border: "none", signin_btn_border_color: "", signin_btn_visible: true,
  signin_btn_margin_top: 0, signin_btn_margin_right: 0, signin_btn_margin_bottom: 0, signin_btn_margin_left: 0,
  cta_text: "Book a Training", cta_link: "/book",
  cta_btn_bg: "#0F172A", cta_btn_color: "#FFFFFF",
  cta_btn_font_size: 14, cta_btn_font_weight: 500, cta_btn_border_radius: 8,
  cta_btn_padding_x: 16, cta_btn_padding_y: 8, cta_btn_visible: true,
  cta_btn_margin_top: 0, cta_btn_margin_right: 0, cta_btn_margin_bottom: 0, cta_btn_margin_left: 0,
  // container
  header_height: null, header_max_width: 1280, header_border_radius: 0,
  nav_padding_top: 10, nav_padding_bottom: 10, nav_padding_left: null, nav_padding_right: null,
  // mobile — enable toggle
  mobile_separate: false,
  mobile_breakpoint: 768,

  // mobile header layout
  mobile_header_height: 56,
  mobile_header_bg: "#FFFFFF",
  mobile_header_padding_top: 10,
  mobile_header_padding_bottom: 10,
  mobile_header_padding_left: 16,
  mobile_header_padding_right: 16,
  mobile_header_max_width: null,
  mobile_header_blur: false,
  mobile_header_shadow: true,
  mobile_header_border: false,
  mobile_header_border_color: "#E2E8F0",
  mobile_header_border_radius: 0,
  mobile_header_gradient: "",

  // mobile logo (independent from desktop)
  mobile_logo_url: "",
  mobile_logo_width: 32,
  mobile_logo_height: 32,
  mobile_logo_border_radius: 8,
  mobile_logo_bg: "",
  mobile_logo_opacity: 1,
  mobile_logo_rotation: 0,
  mobile_logo_shadow: "",
  mobile_logo_visible: true,
  mobile_logo_align: "left",
  mobile_logo_margin_top: 0,
  mobile_logo_margin_right: 8,
  mobile_logo_margin_bottom: 0,
  mobile_logo_margin_left: 0,
  mobile_logo_padding_top: 0,
  mobile_logo_padding_right: 0,
  mobile_logo_padding_bottom: 0,
  mobile_logo_padding_left: 0,

  // mobile company name (independent from desktop)
  mobile_company_name: "",
  mobile_company_name_visible: true,
  mobile_name_font_family: "Inter",
  mobile_name_font_size: 16,
  mobile_name_font_weight: 600,
  mobile_name_font_style: "normal",
  mobile_name_letter_spacing: -0.01,
  mobile_name_line_height: 1.2,
  mobile_name_color: "#0F172A",
  mobile_name_align: "left",
  mobile_name_margin_top: 0,
  mobile_name_margin_right: 0,
  mobile_name_margin_bottom: 0,
  mobile_name_margin_left: 6,
  mobile_name_padding_top: 0,
  mobile_name_padding_right: 0,
  mobile_name_padding_bottom: 0,
  mobile_name_padding_left: 0,

  // mobile hamburger
  mobile_hamburger_color: "#374151",
  mobile_hamburger_size: 20,
  mobile_hamburger_thickness: 2,
  mobile_hamburger_bg: "",
  mobile_hamburger_border_radius: 8,
  mobile_hamburger_position: "right",
  mobile_hamburger_margin_top: 0,
  mobile_hamburger_margin_right: 0,
  mobile_hamburger_margin_bottom: 0,
  mobile_hamburger_margin_left: 0,
  mobile_hamburger_padding: 8,

  // mobile menu drawer
  mobile_menu_bg: "#FFFFFF",
  mobile_menu_position: "left",
  mobile_menu_width: 280,
  mobile_menu_overlay: true,
  mobile_overlay_color: "#000000",
  mobile_overlay_opacity: 0.3,
  mobile_menu_border_radius: 0,
  mobile_menu_shadow: true,
  mobile_menu_animation: "slide",
  mobile_menu_animation_speed: 250,

  // mobile navigation typography
  mobile_nav_font_family: "Inter",
  mobile_nav_font_size: 15,
  mobile_nav_font_weight: 500,
  mobile_nav_color: "#0F172A",
  mobile_nav_item_padding_top: 12,
  mobile_nav_item_padding_bottom: 12,
  mobile_nav_item_padding_left: 12,
  mobile_nav_item_padding_right: 12,
  mobile_nav_item_border_radius: 12,

  // mobile CTA button (independent)
  mobile_show_cta: true,
  mobile_cta_text: "",
  mobile_cta_bg: "#0F172A",
  mobile_cta_color: "#FFFFFF",
  mobile_cta_font_size: 13,
  mobile_cta_font_weight: 600,
  mobile_cta_border_radius: 8,
  mobile_cta_padding_x: 14,
  mobile_cta_padding_y: 8,
  mobile_cta_margin_top: 0,
  mobile_cta_margin_right: 0,
  mobile_cta_margin_bottom: 0,
  mobile_cta_margin_left: 0,

  // mobile sign in (independent)
  mobile_show_signin: true,
  mobile_signin_text: "",
  mobile_signin_color: "#374151",
  mobile_signin_font_size: 14,
  mobile_signin_font_weight: 500,
  mobile_signin_bg: "transparent",
  mobile_signin_border: "none",
  mobile_signin_border_color: "#E2E8F0",
  mobile_signin_border_radius: 8,
  mobile_signin_padding_x: 12,
  mobile_signin_padding_y: 8,
  mobile_signin_width: "",
  mobile_signin_height: "",
  mobile_signin_align: "left",
  // Mobile nav items (independent — null means use nav_items as fallback)
  mobile_nav_items: null,
  mobile_nav_hover_color: "#4F46E5",
  mobile_nav_active_color: "#4F46E5",
  mobile_nav_gap: 4,
  mobile_nav_font_style: "normal",
  mobile_nav_text_decoration: "none",
  // Mobile name underline
  mobile_name_text_decoration: "none",
  // Mobile logo extra
  mobile_logo_max_width: null,
  mobile_logo_max_height: null,
  mobile_logo_min_width: null,
  mobile_logo_min_height: null,
  mobile_logo_scale: 1,
  mobile_logo_offset_x: 0,
  mobile_logo_offset_y: 0,
  mobile_logo_object_fit: "contain",
  mobile_logo_zindex: null,
  // Mobile company name extra
  mobile_tagline: "",
  mobile_tagline_visible: false,
  mobile_name_short: "",
  mobile_name_text_transform: "none",
  mobile_name_text_shadow: "",
  // Mobile header position/visibility
  mobile_header_sticky: true,
  mobile_header_fixed: false,
  mobile_header_transparent: false,
  mobile_header_zindex: 40,
  mobile_header_width: "",
  mobile_container_width: "",
  mobile_header_opacity: 1,
  // Mobile drawer close button
  mobile_drawer_close_btn: true,
  mobile_drawer_close_size: 20,
  // Mobile hamburger extra
  mobile_hamburger_hover_color: "#0F172A",
  mobile_hamburger_animation: "none",
  // Mobile CTA extra
  mobile_cta_border: "none",
  mobile_cta_border_color: "",
  mobile_cta_width: "",
  mobile_cta_height: "",
  mobile_cta_align: "center",
};

// ── Main HeaderBuilder ────────────────────────────────────────────────────────
export default function HeaderBuilder() {
  const { reload, site: liveSite } = useCms();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("logo");
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [logoAspect, setLogoAspect] = useState(true);
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop builder always starts at desktop
  const initRef = useRef(false);
  // Pristine snapshot captured at load time — used by save() to compute exactly
  // which fields this editor session actually changed.
  const baselineRef = useRef(null);

  if (liveSite && !initRef.current) {
    initRef.current = true;
    const init = {
      ...DEFAULTS,
      ...liveSite,
      nav_items: liveSite.nav_items?.length ? liveSite.nav_items : DEFAULT_NAV_ITEMS,
    };
    setD(init);
    setHistory([init]);
    baselineRef.current = init;
  }

  const patch = useCallback((updates) => {
    setD((prev) => {
      const next = { ...prev, ...updates };
      setHistory((h) => [...h.slice(-19), prev]);
      setFuture([]);
      return next;
    });
  }, []);

  const set = (k, v) => patch({ [k]: v });
  const setNum = (k) => (e) => set(k, e.target.value === "" ? null : Number(e.target.value));
  const setStr = (k) => (e) => set(k, e.target.value);
  const setCheck = (k) => (e) => set(k, e.target.checked);

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture((f) => [d, ...f]);
    setHistory((h) => h.slice(0, -1));
    setD(prev);
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory((h) => [...h, d]);
    setFuture((f) => f.slice(1));
    setD(next);
  };

  const save = async () => {
    setLoading(true);
    try {
      // Merge only the fields this editor actually changed onto the latest live
      // document, instead of overwriting the whole record with this session's
      // possibly-stale copy. This is what prevents a desktop-only save from
      // resetting mobile/tablet settings (or any other field) that were
      // updated elsewhere while this page was open.
      const merged = await mergeSaveCmsField("/cms/site", d, baselineRef.current);
      setD(merged);
      baselineRef.current = merged;
      await reload();
      toast.success("Header settings saved");
      setHistory([]);
      setFuture([]);
    } catch { toast.error("Save failed"); }
    setLoading(false);
  };

  const discard = () => {
    if (!liveSite) return;
    const init = { ...DEFAULTS, ...liveSite, nav_items: liveSite.nav_items?.length ? liveSite.nav_items : DEFAULT_NAV_ITEMS };
    setD(init);
    setHistory([]);
    setFuture([]);
    toast("Changes discarded");
  };

  const resetSection = (keys) => {
    const updates = {};
    keys.forEach((k) => { updates[k] = DEFAULTS[k] ?? null; });
    patch(updates);
  };

  const resetToDefault = () => {
    if (!window.confirm("Reset desktop header to default values? Mobile header settings will not be affected.")) return;
    // Only reset desktop keys — never touch mobile_ fields
    const desktopDefaults = Object.fromEntries(
      Object.entries(DEFAULTS).filter(([k]) => !k.startsWith("mobile_"))
    );
    const reset = { ...d, ...desktopDefaults };
    setD(reset);
    setHistory((h) => [...h, d]);
    setFuture([]);
  };

  if (!d) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="fade-up flex flex-col gap-4">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-xl font-semibold text-[#0F172A]">Desktop Header Builder</h1>
          <p className="text-sm text-gray-500 mt-0.5">Controls the desktop header. Changes preview instantly below.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={undo} disabled={!history.length} title="Undo"
            className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50">
            <Undo2 className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={redo} disabled={!future.length} title="Redo"
            className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50">
            <Redo2 className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={resetToDefault} title="Reset to defaults"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50">
            <RefreshCcw className="w-3.5 h-3.5" /> Reset all
          </button>
          <button onClick={discard}
            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            Discard
          </button>
          <button onClick={save} disabled={loading}
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-[#1e293b]">
            <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Live preview */}
      <LiveHeaderPreview d={d} previewMode={previewMode} setPreviewMode={setPreviewMode} />

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 min-w-[80px] inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? "bg-white shadow text-[#0F172A]" : "text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Panel */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        {tab === "logo"    && <LogoTab    d={d} set={set} setNum={setNum} patch={patch} logoAspect={logoAspect} setLogoAspect={setLogoAspect} resetSection={resetSection} />}
        {tab === "name"    && <NameTab    d={d} set={set} setNum={setNum} setStr={setStr} patch={patch} resetSection={resetSection} />}
        {tab === "nav"     && <NavTab     d={d} patch={patch} resetSection={resetSection} />}
        {tab === "buttons" && <ButtonsTab d={d} set={set} setNum={setNum} setStr={setStr} setCheck={setCheck} patch={patch} resetSection={resetSection} />}
        {tab === "spacing" && <SpacingTab d={d} patch={patch} resetSection={resetSection} />}
        {tab === "tablet"  && <TabletTab  d={d} patch={patch} />}
      </div>
    </div>
  );
}

// ── Live header preview ───────────────────────────────────────────────────────
function LiveHeaderPreview({ d, previewMode, setPreviewMode }) {
  const desktopItems = (d.nav_items || DEFAULT_NAV_ITEMS).filter((i) => i.visible && i.device !== "mobile");
  const navStyle = {
    fontSize: d.nav_font_size ? `${d.nav_font_size}px` : undefined,
    fontWeight: d.nav_font_weight ?? 500,
    letterSpacing: d.nav_letter_spacing != null ? `${d.nav_letter_spacing}em` : undefined,
    fontFamily: fontCss(d.nav_font_family || "Inter"),
    color: d.nav_text_color || "#4B5563",
  };
  const isMobile = previewMode === "mobile";
  const isTablet = previewMode === "tablet";
  const bp = d.mobile_breakpoint ?? 768;

  // Tablet preview shows mobile header (below breakpoint) but at ~768px wide
  const showMobileLayout = isMobile || isTablet;

  const previewLabel = isMobile
    ? `Mobile preview (< ${bp}px)`
    : isTablet
      ? `Tablet preview (≈ ${Math.min(bp - 1, 820)}px)`
      : "Desktop preview";

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isMobile
            ? <Smartphone className="w-3.5 h-3.5 text-gray-400" />
            : isTablet
              ? <Tablet className="w-3.5 h-3.5 text-gray-400" />
              : <Monitor className="w-3.5 h-3.5 text-gray-400" />
          }
          <span className="text-xs text-gray-500 font-medium">{previewLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([mode, Icon]) => (
            <button key={mode} onClick={() => setPreviewMode(mode)}
              className={`w-7 h-7 grid place-items-center rounded-md transition-colors ${previewMode === mode ? "bg-[#0F172A] text-white" : "text-gray-400 hover:bg-gray-100"}`}>
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>

      {showMobileLayout ? (
        /* ── Mobile / Tablet preview — uses all mobile-specific CMS values ── */
        (() => {
          const mobileMenuItems = (d.mobile_nav_items || d.nav_items || DEFAULT_NAV_ITEMS)
            .filter(i => i.visible !== false).slice(0, 5);
          const mobileCTAText = d.mobile_cta_text || d.cta_text || "Book";
          const mobileHeaderBg = d.mobile_header_transparent ? "transparent" : (d.mobile_header_gradient || d.mobile_header_bg || "#FFFFFF");
          const mobileNameText = d.mobile_company_name || d.company_name || "";
          const mobileLogoSrc = d.mobile_logo_url;
          return (
            <div style={{
              background: mobileHeaderBg,
              backdropFilter: d.mobile_header_blur ? "blur(12px)" : undefined,
              boxShadow: d.mobile_header_shadow !== false ? "0 1px 3px rgba(0,0,0,0.08)" : undefined,
              borderBottom: d.mobile_header_border ? `1px solid ${d.mobile_header_border_color || "#E2E8F0"}` : undefined,
              borderRadius: d.mobile_header_border_radius || undefined,
              opacity: d.mobile_header_opacity ?? 1,
            }}>
              {/* Header bar */}
              <div className="flex items-center justify-between"
                style={{
                  minHeight: d.mobile_header_height ?? 56,
                  paddingTop: d.mobile_header_padding_top ?? 10,
                  paddingBottom: d.mobile_header_padding_bottom ?? 10,
                  paddingLeft: d.mobile_header_padding_left ?? 16,
                  paddingRight: d.mobile_header_padding_right ?? 16,
                  maxWidth: d.mobile_header_max_width || undefined,
                }}>
                {/* Logo + name */}
                <div className="flex items-center shrink-0 min-w-0" style={{
                  gap: 6,
                  justifyContent: d.mobile_logo_align === "center" ? "center" : d.mobile_logo_align === "right" ? "flex-end" : "flex-start",
                }}>
                  {d.mobile_logo_visible !== false && (
                    mobileLogoSrc ? (
                      <img src={mobileLogoSrc.startsWith("http") ? mobileLogoSrc : `${process.env.REACT_APP_BACKEND_URL || ""}${mobileLogoSrc}`}
                        alt="Mobile logo" style={{
                          width: d.mobile_logo_width ?? 32,
                          height: d.mobile_logo_height ?? 32,
                          minWidth: d.mobile_logo_min_width ? `${d.mobile_logo_min_width}px` : undefined,
                          minHeight: d.mobile_logo_min_height ? `${d.mobile_logo_min_height}px` : undefined,
                          maxWidth: d.mobile_logo_max_width ? `${d.mobile_logo_max_width}px` : undefined,
                          maxHeight: d.mobile_logo_max_height ? `${d.mobile_logo_max_height}px` : undefined,
                          borderRadius: d.mobile_logo_border_radius ?? 8,
                          objectFit: d.mobile_logo_object_fit || "contain",
                          opacity: d.mobile_logo_opacity ?? 1,
                          background: d.mobile_logo_bg || undefined,
                          boxShadow: d.mobile_logo_shadow || undefined,
                          transform: [
                            d.mobile_logo_rotation ? `rotate(${d.mobile_logo_rotation}deg)` : "",
                            d.mobile_logo_scale && d.mobile_logo_scale !== 1 ? `scale(${d.mobile_logo_scale})` : "",
                            (d.mobile_logo_offset_x || d.mobile_logo_offset_y) ? `translate(${d.mobile_logo_offset_x ?? 0}px,${d.mobile_logo_offset_y ?? 0}px)` : "",
                          ].filter(Boolean).join(" ") || undefined,
                          marginTop: d.mobile_logo_margin_top || undefined,
                          marginRight: d.mobile_logo_margin_right ?? 8,
                          marginBottom: d.mobile_logo_margin_bottom || undefined,
                          marginLeft: d.mobile_logo_margin_left || undefined,
                          paddingTop: d.mobile_logo_padding_top || undefined,
                          paddingRight: d.mobile_logo_padding_right || undefined,
                          paddingBottom: d.mobile_logo_padding_bottom || undefined,
                          paddingLeft: d.mobile_logo_padding_left || undefined,
                        }} />
                    ) : (
                      /* No mobile logo set — show placeholder, NEVER desktop logo */
                      <div style={{
                        width: d.mobile_logo_width ?? 32,
                        height: d.mobile_logo_height ?? 32,
                        borderRadius: d.mobile_logo_border_radius ?? 8,
                        background: "#E5E7EB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: "#9CA3AF",
                      }}>Logo</div>
                    )
                  )}
                  {d.mobile_company_name_visible !== false && mobileNameText && (
                    <div>
                      <span style={{
                        fontFamily: d.mobile_name_font_family ? `'${d.mobile_name_font_family}', sans-serif` : undefined,
                        fontSize: d.mobile_name_font_size ?? 16,
                        fontWeight: d.mobile_name_font_weight ?? 600,
                        fontStyle: d.mobile_name_font_style || undefined,
                        color: d.mobile_name_color || "#0F172A",
                        letterSpacing: d.mobile_name_letter_spacing ? `${d.mobile_name_letter_spacing}em` : undefined,
                        textTransform: d.mobile_name_text_transform !== "none" ? d.mobile_name_text_transform : undefined,
                        textShadow: d.mobile_name_text_shadow || undefined,
                        display: "block",
                      }}>{mobileNameText}</span>
                      {d.mobile_tagline_visible && d.mobile_tagline && (
                        <span style={{ fontSize: 10, color: "#94A3B8", display: "block" }}>{d.mobile_tagline}</span>
                      )}
                    </div>
                  )}
                </div>
                {/* CTA + hamburger */}
                <div className="flex items-center gap-2 shrink-0">
                  {d.mobile_show_cta && (
                    <span className="cursor-default text-xs font-semibold" style={{
                      background: d.mobile_cta_bg || d.cta_btn_bg || "#0F172A",
                      color: d.mobile_cta_color || "#FFFFFF",
                      fontSize: d.mobile_cta_font_size ?? 13,
                      fontWeight: d.mobile_cta_font_weight ?? 600,
                      borderRadius: d.mobile_cta_border_radius ?? 8,
                      paddingLeft: d.mobile_cta_padding_x ?? 14,
                      paddingRight: d.mobile_cta_padding_x ?? 14,
                      paddingTop: d.mobile_cta_padding_y ?? 8,
                      paddingBottom: d.mobile_cta_padding_y ?? 8,
                      border: d.mobile_cta_border && d.mobile_cta_border !== "none"
                        ? `1px solid ${d.mobile_cta_border_color || "#E2E8F0"}` : undefined,
                    }}>{mobileCTAText}</span>
                  )}
                  <div style={{
                    padding: d.mobile_hamburger_padding ?? 8,
                    background: d.mobile_hamburger_bg || "transparent",
                    borderRadius: d.mobile_hamburger_border_radius ?? 8,
                    cursor: "default",
                  }}>
                    {[0,1,2].map((i) => (
                      <div key={i} style={{
                        width: d.mobile_hamburger_size ?? 20,
                        height: d.mobile_hamburger_thickness ?? 2,
                        background: d.mobile_hamburger_color || "#374151",
                        borderRadius: 2,
                        marginBottom: i < 2 ? (d.mobile_hamburger_thickness ?? 2) + 2 : 0,
                      }} />
                    ))}
                  </div>
                </div>
              </div>
              {/* Drawer preview */}
              <div className="border-t border-gray-100 py-2"
                style={{ background: d.mobile_menu_bg || "#FFFFFF", borderRadius: d.mobile_menu_border_radius || undefined }}>
                <div className="text-[10px] text-gray-400 mb-1 px-3">Drawer preview</div>
                {d.mobile_drawer_close_btn !== false && (
                  <div className="flex justify-end px-3 mb-1">
                    <span style={{ fontSize: d.mobile_drawer_close_size ?? 20, color: "#94A3B8", cursor: "default" }}>✕</span>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: d.mobile_nav_gap != null ? `${d.mobile_nav_gap}px` : "2px" }}>
                {mobileMenuItems.map((it, i) => (
                  <div key={it.id || i} style={{
                    fontFamily: d.mobile_nav_font_family ? `'${d.mobile_nav_font_family}', sans-serif` : undefined,
                    fontSize: d.mobile_nav_font_size ?? 15,
                    fontWeight: d.mobile_nav_font_weight ?? 500,
                    fontStyle: d.mobile_nav_font_style && d.mobile_nav_font_style !== "normal" ? d.mobile_nav_font_style : undefined,
                    textDecoration: d.mobile_nav_text_decoration && d.mobile_nav_text_decoration !== "none" ? d.mobile_nav_text_decoration : undefined,
                    color: d.mobile_nav_color || "#0F172A",
                    paddingTop: d.mobile_nav_item_padding_top ?? 12,
                    paddingBottom: d.mobile_nav_item_padding_bottom ?? 12,
                    paddingLeft: d.mobile_nav_item_padding_left ?? 12,
                    paddingRight: d.mobile_nav_item_padding_right ?? 12,
                    borderRadius: d.mobile_nav_item_border_radius ?? 12,
                    cursor: "default",
                  }}>{it.label}</div>
                ))}
                </div>
                {d.mobile_show_signin !== false && (
                  <div style={{
                    fontSize: d.mobile_signin_font_size ?? 14,
                    fontWeight: d.mobile_signin_font_weight ?? 500,
                    color: d.mobile_signin_color || "#374151",
                    background: d.mobile_signin_bg && d.mobile_signin_bg !== "transparent" ? d.mobile_signin_bg : undefined,
                    padding: `${d.mobile_signin_padding_y ?? 8}px ${d.mobile_signin_padding_x ?? 12}px`,
                    borderRadius: d.mobile_signin_border_radius ?? 8,
                    border: d.mobile_signin_border && d.mobile_signin_border !== "none" ? `1px solid ${d.mobile_signin_border_color || "#E2E8F0"}` : undefined,
                    margin: "4px 12px",
                    cursor: "default",
                  }}>{d.mobile_signin_text || d.signin_btn_text || "Sign in"}</div>
                )}
              </div>
            </div>
          );
        })()
      ) : (
        /* ── Desktop preview ── */
        <div className="overflow-x-auto">
          <div
            className="flex items-center gap-3 border-b border-gray-100 bg-white/90 backdrop-blur-sm"
            style={{
              minHeight: d.header_height ?? 56,
              maxWidth: "100%",
              paddingTop: d.nav_padding_top ?? 8,
              paddingBottom: d.nav_padding_bottom ?? 8,
              paddingLeft: d.nav_padding_left ?? 16,
              paddingRight: d.nav_padding_right ?? 16,
              borderRadius: d.header_border_radius || undefined,
            }}
          >
            {/* Logo */}
            <div
              onClick={(e) => e.preventDefault()}
              style={{
                marginTop: d.logo_margin_top || undefined,
                marginBottom: d.logo_margin_bottom || undefined,
                paddingTop: d.logo_padding_top || undefined,
                paddingRight: d.logo_padding_right || undefined,
                paddingBottom: d.logo_padding_bottom || undefined,
                paddingLeft: d.logo_padding_left || undefined,
              }}
              className="shrink-0"
            >
              <Logo previewSite={d} />
            </div>

            {/* Nav items */}
            <div
              className="hidden md:flex flex-1 items-center flex-nowrap overflow-x-auto"
              style={{
                gap: d.nav_gap ?? 4,
                marginTop: d.nav_margin_top || undefined,
                marginRight: d.nav_margin_right || undefined,
                marginBottom: d.nav_margin_bottom || undefined,
                marginLeft: d.nav_margin_left || undefined,
              }}
            >
              {desktopItems.map((it) => (
                <span key={it.id} className="text-sm cursor-default whitespace-nowrap shrink-0"
                  style={{
                    ...navStyle,
                    paddingTop: d.nav_item_padding_top ?? 8,
                    paddingRight: d.nav_item_padding_right ?? 12,
                    paddingBottom: d.nav_item_padding_bottom ?? 8,
                    paddingLeft: d.nav_item_padding_left ?? 12,
                    borderRadius: `${d.nav_item_border_radius ?? 8}px`,
                  }}>{it.label}</span>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {!d.hide_login && d.signin_btn_visible !== false && (
                <span className="text-sm cursor-default" style={{
                  color: d.signin_btn_color || "#374151",
                  fontSize: d.signin_btn_font_size ? `${d.signin_btn_font_size}px` : undefined,
                  fontWeight: d.signin_btn_font_weight ?? 500,
                  background: d.signin_btn_bg || "transparent",
                  borderRadius: d.signin_btn_border_radius ? `${d.signin_btn_border_radius}px` : undefined,
                  paddingLeft: d.signin_btn_padding_x ? `${d.signin_btn_padding_x}px` : 12,
                  paddingRight: d.signin_btn_padding_x ? `${d.signin_btn_padding_x}px` : 12,
                  paddingTop: d.signin_btn_padding_y ? `${d.signin_btn_padding_y}px` : 8,
                  paddingBottom: d.signin_btn_padding_y ? `${d.signin_btn_padding_y}px` : 8,
                  border: d.signin_btn_border && d.signin_btn_border !== "none"
                    ? `1px solid ${d.signin_btn_border_color || "#E2E8F0"}` : undefined,
                  marginTop: d.signin_btn_margin_top || undefined,
                  marginRight: d.signin_btn_margin_right || undefined,
                  marginBottom: d.signin_btn_margin_bottom || undefined,
                  marginLeft: d.signin_btn_margin_left || undefined,
                }}>
                  {d.signin_btn_text || "Sign in"}
                </span>
              )}
              {d.cta_btn_visible !== false && (
                <span className="text-sm cursor-default" style={{
                  background: d.cta_btn_bg || "#0F172A",
                  color: d.cta_btn_color || "#FFFFFF",
                  fontSize: d.cta_btn_font_size ? `${d.cta_btn_font_size}px` : undefined,
                  fontWeight: d.cta_btn_font_weight ?? 500,
                  borderRadius: d.cta_btn_border_radius ? `${d.cta_btn_border_radius}px` : 8,
                  paddingLeft: d.cta_btn_padding_x ? `${d.cta_btn_padding_x}px` : 16,
                  paddingRight: d.cta_btn_padding_x ? `${d.cta_btn_padding_x}px` : 16,
                  paddingTop: d.cta_btn_padding_y ? `${d.cta_btn_padding_y}px` : 8,
                  paddingBottom: d.cta_btn_padding_y ? `${d.cta_btn_padding_y}px` : 8,
                  marginTop: d.cta_btn_margin_top || undefined,
                  marginRight: d.cta_btn_margin_right || undefined,
                  marginBottom: d.cta_btn_margin_bottom || undefined,
                  marginLeft: d.cta_btn_margin_left || undefined,
                }}>
                  {d.cta_text || "Book a Training"}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Logo tab ──────────────────────────────────────────────────────────────────
function LogoTab({ d, set, setNum, patch, logoAspect, setLogoAspect, resetSection }) {
  const handleWidth = (e) => {
    const w = Number(e.target.value);
    if (logoAspect && d.logo_width && d.logo_height) {
      const ratio = d.logo_height / d.logo_width;
      patch({ logo_width: w, logo_height: Math.round(w * ratio) });
    } else { set("logo_width", w); }
  };
  const handleHeight = (e) => {
    const h = Number(e.target.value);
    if (logoAspect && d.logo_width && d.logo_height) {
      const ratio = d.logo_width / d.logo_height;
      patch({ logo_height: h, logo_width: Math.round(h * ratio) });
    } else { set("logo_height", h); }
  };

  return (
    <div className="grid gap-6 max-w-2xl">
      <Section title="Logo upload">
        <ImageUpload value={d.logo_url} onChange={(v) => set("logo_url", v)} testid="header-logo-upload" accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp" />
        {d.logo_url && (
          <button onClick={() => set("logo_url", "")}
            className="inline-flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-medium">
            <Trash2 className="w-3.5 h-3.5" /> Remove logo
          </button>
        )}
      </Section>

      <Section title="Logo size">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500 font-medium">Aspect ratio</span>
          <button onClick={() => setLogoAspect((v) => !v)}
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${logoAspect ? "bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
            {logoAspect ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            {logoAspect ? "Locked" : "Free"}
          </button>
        </div>
        <SliderInput label="Width" value={d.logo_width ?? 32} min={8} max={200} onChange={handleWidth} />
        <SliderInput label="Height" value={d.logo_height ?? 32} min={8} max={200} onChange={handleHeight} />
        <SliderInput label="Border radius" value={d.logo_radius ?? 8} min={0} max={100}
          onChange={(e) => set("logo_radius", Number(e.target.value))} />
        <SliderInput label="Scale" value={Math.round((d.logo_scale ?? 1) * 100)} min={50} max={200}
          onChange={(e) => set("logo_scale", Number(e.target.value) / 100)} />
      </Section>

      <Section title="Logo position">
        <SliderInput label="Logo–name gap" value={d.logo_name_gap ?? 8} min={0} max={60}
          onChange={(e) => set("logo_name_gap", Number(e.target.value))} />
        <SliderInput label="Left spacing" value={d.logo_left_spacing ?? 0} min={-50} max={100}
          onChange={(e) => set("logo_left_spacing", Number(e.target.value))} />
        <SliderInput label="Top offset" value={d.logo_top_offset ?? 0} min={-20} max={20}
          onChange={(e) => set("logo_top_offset", Number(e.target.value))} />
        <SliderInput label="Right margin" value={d.logo_margin_right ?? 8} min={0} max={60}
          onChange={(e) => set("logo_margin_right", Number(e.target.value))} />
        <div>
          <Lbl>Fine position (X / Y)</Lbl>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Horizontal offset</Lbl>
              <div className="flex items-center gap-1">
                <NudgeBtn onClick={() => set("logo_offset_x", (d.logo_offset_x ?? 0) - 1)}>←</NudgeBtn>
                <input type="number" value={d.logo_offset_x ?? 0}
                  onChange={(e) => set("logo_offset_x", Number(e.target.value))}
                  className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs text-center outline-none" />
                <NudgeBtn onClick={() => set("logo_offset_x", (d.logo_offset_x ?? 0) + 1)}>→</NudgeBtn>
              </div>
            </div>
            <div>
              <Lbl>Vertical offset</Lbl>
              <div className="flex items-center gap-1">
                <NudgeBtn onClick={() => set("logo_offset_y", (d.logo_offset_y ?? 0) - 1)}>↑</NudgeBtn>
                <input type="number" value={d.logo_offset_y ?? 0}
                  onChange={(e) => set("logo_offset_y", Number(e.target.value))}
                  className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs text-center outline-none" />
                <NudgeBtn onClick={() => set("logo_offset_y", (d.logo_offset_y ?? 0) + 1)}>↓</NudgeBtn>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ── Company name tab ──────────────────────────────────────────────────────────
function NameTab({ d, set, setNum, setStr, patch, resetSection }) {
  return (
    <div className="grid gap-6 max-w-2xl">
      <Section title="Show company name in header">
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
          Off by default. The "Company name" text below is also used for SEO and structured data —
          turning this on is what actually makes text appear next to the logo in the header.
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={d.header_company_name_visible === true}
            onChange={(e) => set("header_company_name_visible", e.target.checked)} />
          Show company name next to the logo (desktop &amp; tablet)
        </label>
        <Field label="Header override text (optional — leave blank to use Company name below)">
          <input value={d.header_company_name || ""} onChange={setStr("header_company_name")}
            placeholder="Leave blank to reuse the company name"
            className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm w-full" />
        </Field>
      </Section>

      <Section title="Company name text">
        <Field label="Company name (supports line breaks with Enter)">
          <textarea value={d.company_name || ""} onChange={setStr("company_name")} rows={3}
            placeholder="Enter company name…"
            className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm w-full resize-y" />
        </Field>
      </Section>

      <Section title="Font">
        <Field label="Font family">
          <Select value={d.company_name_font_family || "Inter"} onChange={setStr("company_name_font_family")}>
            {FONT_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.fonts.map((f) => <option key={f} value={f} style={{ fontFamily: fontCss(f) }}>{f}</option>)}
              </optgroup>
            ))}
          </Select>
        </Field>
        <div className="px-3 py-3 rounded-xl bg-gray-50 text-center"
          style={{ fontFamily: fontCss(d.company_name_font_family || "Inter"), fontSize: 18, color: d.company_name_color || "#0F172A", fontWeight: d.company_name_font_weight ?? 600 }}>
          {d.company_name || "Company Name Preview"}
        </div>
        <Row cols={2}>
          <Field label="Font size (px)">
            <NumInput value={d.company_name_font_size ?? 18} min={8} max={72} onChange={setNum("company_name_font_size")} />
          </Field>
          <Field label="Font weight">
            <Select value={d.company_name_font_weight ?? 600} onChange={(e) => set("company_name_font_weight", Number(e.target.value))}>
              {[300, 400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
            </Select>
          </Field>
        </Row>
        <Row cols={2}>
          <ColorField label="Font color" value={d.company_name_color || "#0F172A"}
            onChange={(e) => set("company_name_color", e.target.value)} />
          <Field label="Text transform">
            <Select value={d.company_name_text_transform || "none"} onChange={setStr("company_name_text_transform")}>
              <option value="none">None</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="capitalize">Capitalize</option>
            </Select>
          </Field>
        </Row>
        <Row cols={2}>
          <Field label="Letter spacing (em)">
            <NumInput value={d.company_name_letter_spacing ?? -0.01} step={0.01} min={-0.2} max={0.5}
              onChange={setNum("company_name_letter_spacing")} />
          </Field>
          <Field label="Text alignment">
            <Select value={d.company_name_text_align || "left"} onChange={setStr("company_name_text_align")}>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </Select>
          </Field>
        </Row>
        <Row cols={2}>
          <Field label="Vertical alignment">
            <Select value={d.company_name_valign || "center"} onChange={setStr("company_name_valign")}>
              <option value="top">Top</option>
              <option value="center">Center</option>
              <option value="bottom">Bottom</option>
            </Select>
          </Field>
          <Field label="Opacity (0–1)">
            <NumInput value={d.company_name_opacity ?? 1} step={0.05} min={0} max={1}
              onChange={setNum("company_name_opacity")} />
          </Field>
        </Row>
      </Section>

      <Section title="Tagline">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!d.tagline_show} onChange={(e) => set("tagline_show", e.target.checked)} />
          Show tagline below company name
        </label>
        <Field label="Tagline text">
          <TextInput value={d.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="e.g. Teacher Training Institute" />
        </Field>
        <Row cols={2}>
          <Field label="Font size (px)">
            <NumInput value={d.tagline_font_size ?? 11} min={8} max={24} onChange={setNum("tagline_font_size")} />
          </Field>
          <ColorField label="Tagline color" value={d.tagline_color || "#94A3B8"}
            onChange={(e) => set("tagline_color", e.target.value)} />
        </Row>
        <Row cols={2}>
          <Field label="Font weight">
            <Select value={d.tagline_font_weight ?? 400} onChange={(e) => set("tagline_font_weight", Number(e.target.value))}>
              {[300, 400, 500, 600].map((w) => <option key={w} value={w}>{w}</option>)}
            </Select>
          </Field>
          <Field label="Letter spacing (em)">
            <NumInput value={d.tagline_letter_spacing ?? 0} step={0.01} min={-0.1} max={0.5}
              onChange={setNum("tagline_letter_spacing")} />
          </Field>
        </Row>
      </Section>
    </div>
  );
}

// ── Navigation tab ────────────────────────────────────────────────────────────
function NavTab({ d, patch, resetSection }) {
  const items = d.nav_items || DEFAULT_NAV_ITEMS;
  const dragIdx = useRef(null);

  const updateItem = (idx, updates) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...updates } : it);
    patch({ nav_items: next });
  };
  const removeItem = (idx) => { patch({ nav_items: items.filter((_, i) => i !== idx) }); };
  const addItem = () => {
    patch({ nav_items: [...items, { id: `n${Date.now()}`, label: "New Link", to: "/", visible: true, device: "all", target: "_self" }] });
  };
  const moveUp   = (idx) => { if (idx === 0) return; const a = [...items]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; patch({ nav_items: a }); };
  const moveDown = (idx) => { if (idx === items.length-1) return; const a = [...items]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; patch({ nav_items: a }); };
  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const a = [...items];
    const [moved] = a.splice(dragIdx.current, 1);
    a.splice(idx, 0, moved);
    dragIdx.current = idx;
    patch({ nav_items: a });
  };

  return (
    <div className="grid gap-6 max-w-2xl">
      <Section title="Navigation typography">
        <Field label="Font family">
          <Select value={d.nav_font_family || "Inter"} onChange={(e) => patch({ nav_font_family: e.target.value })}>
            {FONT_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.fonts.map((f) => <option key={f} value={f}>{f}</option>)}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Row cols={2}>
          <Field label="Font size (px)">
            <NumInput value={d.nav_font_size ?? 14} min={10} max={24}
              onChange={(e) => patch({ nav_font_size: Number(e.target.value) })} />
          </Field>
          <Field label="Font weight">
            <Select value={d.nav_font_weight ?? 500} onChange={(e) => patch({ nav_font_weight: Number(e.target.value) })}>
              {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
            </Select>
          </Field>
        </Row>
        <Row cols={2}>
          <Field label="Letter spacing (em)">
            <NumInput value={d.nav_letter_spacing ?? 0} step={0.01} min={-0.1} max={0.3}
              onChange={(e) => patch({ nav_letter_spacing: Number(e.target.value) })} />
          </Field>
          <Field label="Item gap (px)">
            <NumInput value={d.nav_gap ?? 4} min={0} max={40}
              onChange={(e) => patch({ nav_gap: Number(e.target.value) })} />
          </Field>
        </Row>
        <Row cols={3}>
          <ColorField label="Text color" value={d.nav_text_color || "#4B5563"}
            onChange={(e) => patch({ nav_text_color: e.target.value })} />
          <ColorField label="Hover color" value={d.nav_hover_color || "#0F172A"}
            onChange={(e) => patch({ nav_hover_color: e.target.value })} />
          <ColorField label="Active color" value={d.nav_active_color || "#0F172A"}
            onChange={(e) => patch({ nav_active_color: e.target.value })} />
        </Row>
      </Section>

      <Section title={`Menu items (${items.length})`}>
        <p className="text-xs text-gray-400">Drag to reorder. Use Up/Down for fine control.</p>
        <div className="grid gap-2">
          {items.map((it, idx) => (
            <div key={it.id} draggable onDragStart={() => onDragStart(idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              className={`rounded-xl border ${it.visible ? "border-gray-200 bg-white" : "border-dashed border-gray-200 bg-gray-50"} p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing shrink-0" />
                <span className="text-xs font-medium text-gray-400 w-5 shrink-0">{idx + 1}</span>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input value={it.label} onChange={(e) => updateItem(idx, { label: e.target.value })}
                    placeholder="Label" className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none w-full" />
                  <input value={it.to} onChange={(e) => updateItem(idx, { to: e.target.value })}
                    placeholder="/path or https://…" className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none w-full font-mono" />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => moveUp(idx)} disabled={idx === 0}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveDown(idx)} disabled={idx === items.length - 1}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => updateItem(idx, { visible: !it.visible })}
                    title={it.visible ? "Hide" : "Show"}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 bg-white hover:bg-gray-50">
                    {it.visible ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                  </button>
                  <button onClick={() => removeItem(idx)}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 bg-white hover:bg-rose-50 hover:text-rose-600 text-gray-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 pl-9">
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={!!it.visible} onChange={(e) => updateItem(idx, { visible: e.target.checked })} />
                  Visible
                </label>
                <Select value={it.device || "all"} onChange={(e) => updateItem(idx, { device: e.target.value })}
                  className="!w-auto text-xs py-1 px-2 h-7">
                  <option value="all">All devices</option>
                  <option value="desktop">Desktop only</option>
                  <option value="mobile">Mobile only</option>
                </Select>
                <Select value={it.target || "_self"} onChange={(e) => updateItem(idx, { target: e.target.value })}
                  className="!w-auto text-xs py-1 px-2 h-7">
                  <option value="_self">Same tab</option>
                  <option value="_blank">New tab</option>
                </Select>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addItem}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]">
          <Plus className="w-4 h-4" /> Add menu item
        </button>
      </Section>
    </div>
  );
}

// ── Buttons tab ───────────────────────────────────────────────────────────────
function BtnSection({ title, d, prefix, setField, children }) {
  return (
    <Section title={title}>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={d[`${prefix}_visible`] !== false}
          onChange={(e) => setField(`${prefix}_visible`, e.target.checked)} />
        Visible
      </label>
      <Field label="Button text">
        <TextInput value={d[prefix === "signin_btn" ? "signin_btn_text" : "cta_text"] || ""}
          onChange={(e) => setField(prefix === "signin_btn" ? "signin_btn_text" : "cta_text", e.target.value)} />
      </Field>
      {prefix !== "signin_btn" && (
        <Field label="Button link">
          <TextInput value={d.cta_link || ""} onChange={(e) => setField("cta_link", e.target.value)} />
        </Field>
      )}
      <Row cols={2}>
        <ColorField label="Background" value={d[`${prefix}_bg`] || (prefix === "signin_btn" ? "transparent" : "#0F172A")}
          onChange={(e) => setField(`${prefix}_bg`, e.target.value)} />
        <ColorField label="Text color" value={d[`${prefix}_color`] || (prefix === "signin_btn" ? "#374151" : "#FFFFFF")}
          onChange={(e) => setField(`${prefix}_color`, e.target.value)} />
      </Row>
      <Row cols={2}>
        <Field label="Font size (px)">
          <NumInput value={d[`${prefix}_font_size`] ?? 14} min={10} max={24}
            onChange={(e) => setField(`${prefix}_font_size`, Number(e.target.value))} />
        </Field>
        <Field label="Font weight">
          <Select value={d[`${prefix}_font_weight`] ?? 500} onChange={(e) => setField(`${prefix}_font_weight`, Number(e.target.value))}>
            {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
          </Select>
        </Field>
      </Row>
      <Row cols={2}>
        <Field label="H padding (px)">
          <NumInput value={d[`${prefix}_padding_x`] ?? (prefix === "signin_btn" ? 12 : 16)} min={0} max={60}
            onChange={(e) => setField(`${prefix}_padding_x`, Number(e.target.value))} />
        </Field>
        <Field label="V padding (px)">
          <NumInput value={d[`${prefix}_padding_y`] ?? 8} min={0} max={40}
            onChange={(e) => setField(`${prefix}_padding_y`, Number(e.target.value))} />
        </Field>
      </Row>
      <Field label="Border radius (px)">
        <NumInput value={d[`${prefix}_border_radius`] ?? 8} min={0} max={50}
          onChange={(e) => setField(`${prefix}_border_radius`, Number(e.target.value))} />
      </Field>
      {prefix === "signin_btn" && (
        <Row cols={2}>
          <Field label="Border style">
            <Select value={d.signin_btn_border || "none"} onChange={(e) => setField("signin_btn_border", e.target.value)}>
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </Select>
          </Field>
          {d.signin_btn_border && d.signin_btn_border !== "none" && (
            <ColorField label="Border color" value={d.signin_btn_border_color || "#E2E8F0"}
              onChange={(e) => setField("signin_btn_border_color", e.target.value)} />
          )}
        </Row>
      )}
      {children}
    </Section>
  );
}

function ButtonsTab({ d, set, setNum, setStr, setCheck, patch, resetSection }) {
  const setField = (k, v) => set(k, v);
  return (
    <div className="grid gap-6 max-w-2xl">
      <BtnSection title="Sign In button" d={d} prefix="signin_btn" setField={setField}>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!d.hide_login} onChange={(e) => set("hide_login", !e.target.checked)} />
          Show Sign In button
        </label>
      </BtnSection>
      <BtnSection title="Book Now / CTA button" d={d} prefix="cta_btn" setField={setField} />
    </div>
  );
}

// ── Spacing tab ───────────────────────────────────────────────────────────────
function SpacingTab({ d, patch, resetSection }) {
  return (
    <div className="grid gap-6 max-w-2xl">
      <Section title="Header container padding">
        <div className="grid grid-cols-2 gap-4">
          <SliderInput label="Top" value={d.nav_padding_top ?? 10} min={0} max={60}
            onChange={(e) => patch({ nav_padding_top: Number(e.target.value) })} />
          <SliderInput label="Bottom" value={d.nav_padding_bottom ?? 10} min={0} max={60}
            onChange={(e) => patch({ nav_padding_bottom: Number(e.target.value) })} />
          <SliderInput label="Left" value={d.nav_padding_left ?? 0} min={0} max={120}
            onChange={(e) => patch({ nav_padding_left: Number(e.target.value) || null })} />
          <SliderInput label="Right" value={d.nav_padding_right ?? 0} min={0} max={120}
            onChange={(e) => patch({ nav_padding_right: Number(e.target.value) || null })} />
        </div>
        <Row cols={2}>
          <Field label="Height (px)">
            <NumInput value={d.header_height ?? ""} placeholder="auto" min={32} max={200}
              onChange={(e) => patch({ header_height: e.target.value ? Number(e.target.value) : null })} />
          </Field>
          <Field label="Max width (px)">
            <NumInput value={d.header_max_width ?? ""} placeholder="1280" min={600} max={2560}
              onChange={(e) => patch({ header_max_width: e.target.value ? Number(e.target.value) : null })} />
          </Field>
        </Row>
        <Field label="Border radius (px)">
          <NumInput value={d.header_border_radius ?? 0} min={0} max={50}
            onChange={(e) => patch({ header_border_radius: Number(e.target.value) })} />
        </Field>
        <button onClick={() => patch({ nav_padding_top: 10, nav_padding_bottom: 10, nav_padding_left: null, nav_padding_right: null })}
          className="text-xs text-[#4F46E5] hover:underline">Reset padding to default</button>
      </Section>

      <Section title="Logo spacing">
        <SpacingBox label="Margin" prefix="logo_margin" d={d} patch={patch} />
        <SpacingBox label="Padding" prefix="logo_padding" d={d} patch={patch} />
      </Section>

      <Section title="Company name spacing">
        <SpacingBox label="Margin" prefix="company_name_margin" d={d} patch={patch} />
        <SpacingBox label="Padding" prefix="company_name_padding" d={d} patch={patch} />
      </Section>

      <Section title="Navigation menu spacing">
        <SpacingBox label="Nav bar margin" prefix="nav_margin" d={d} patch={patch} />
        <div className="mt-2">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">Nav item style</div>
          <div className="grid gap-3 mb-4">
            <SliderInput label="Item border radius (px)" value={d.nav_item_border_radius ?? 8} min={0} max={50}
              onChange={(e) => patch({ nav_item_border_radius: Number(e.target.value) })} />
          </div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">Nav item padding (per item)</div>
          <div className="grid grid-cols-2 gap-4">
            <SliderInput label="Top" value={d.nav_item_padding_top ?? 8} min={0} max={40}
              onChange={(e) => patch({ nav_item_padding_top: Number(e.target.value) })} />
            <SliderInput label="Bottom" value={d.nav_item_padding_bottom ?? 8} min={0} max={40}
              onChange={(e) => patch({ nav_item_padding_bottom: Number(e.target.value) })} />
            <SliderInput label="Left" value={d.nav_item_padding_left ?? 12} min={0} max={40}
              onChange={(e) => patch({ nav_item_padding_left: Number(e.target.value) })} />
            <SliderInput label="Right" value={d.nav_item_padding_right ?? 12} min={0} max={40}
              onChange={(e) => patch({ nav_item_padding_right: Number(e.target.value) })} />
          </div>
        </div>
      </Section>

      <Section title="Sign In button margin">
        <SpacingBox label="Margin" prefix="signin_btn_margin" d={d} patch={patch} />
      </Section>

      <Section title="CTA button margin">
        <SpacingBox label="Margin" prefix="cta_btn_margin" d={d} patch={patch} />
      </Section>
    </div>
  );
}

// ── Mobile tab ────────────────────────────────────────────────────────────────
// ── Mobile Navigation Items Editor ───────────────────────────────────────────
// Independent nav items for mobile — stored as mobile_nav_items in CMS.
// Falls back to nav_items (desktop) if mobile_nav_items is null/empty.
function MobileNavItemsEditor({ d, patch }) {
  const dragIdx = useRef(null);
  const effectiveItems = d.mobile_nav_items || (d.nav_items || DEFAULT_NAV_ITEMS);
  const isIndependent = !!d.mobile_nav_items;

  const initIndependent = () => {
    patch({ mobile_nav_items: JSON.parse(JSON.stringify(d.nav_items || DEFAULT_NAV_ITEMS)) });
  };
  const clearIndependent = () => {
    if (window.confirm("Reset mobile navigation to use desktop nav items?")) patch({ mobile_nav_items: null });
  };

  const updateItem = (idx, updates) => {
    const next = effectiveItems.map((it, i) => i === idx ? { ...it, ...updates } : it);
    patch({ mobile_nav_items: next });
  };
  const removeItem = (idx) => patch({ mobile_nav_items: effectiveItems.filter((_, i) => i !== idx) });
  const addItem = () => patch({ mobile_nav_items: [...effectiveItems, { id: `mn${Date.now()}`, label: "New Link", to: "/", visible: true }] });
  const moveUp   = (idx) => { if (idx === 0) return; const a = [...effectiveItems]; [a[idx-1],a[idx]]=[a[idx],a[idx-1]]; patch({ mobile_nav_items: a }); };
  const moveDown = (idx) => { if (idx===effectiveItems.length-1) return; const a=[...effectiveItems]; [a[idx],a[idx+1]]=[a[idx+1],a[idx]]; patch({ mobile_nav_items: a }); };
  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const a = [...effectiveItems];
    const [m] = a.splice(dragIdx.current, 1);
    a.splice(idx, 0, m);
    dragIdx.current = idx;
    patch({ mobile_nav_items: a });
  };

  return (
    <Section title="Mobile menu items">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">{isIndependent ? "Independent mobile navigation (separate from desktop)." : "Currently using desktop nav items."}</p>
        {isIndependent
          ? <button type="button" onClick={clearIndependent} className="text-xs text-rose-600 hover:underline">Reset to desktop nav</button>
          : <button type="button" onClick={initIndependent} className="text-xs text-[#4F46E5] hover:underline font-medium">Make independent</button>
        }
      </div>
      <div className="grid gap-2">
        {effectiveItems.map((it, idx) => (
          <div key={it.id || idx} draggable onDragStart={() => onDragStart(idx)} onDragOver={(e) => onDragOver(e, idx)}
            className={`rounded-xl border ${it.visible !== false ? "border-gray-200 bg-white" : "border-dashed border-gray-200 bg-gray-50"} p-3`}>
            <div className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0" />
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input value={it.label} onChange={(e) => { if (isIndependent) updateItem(idx, { label: e.target.value }); }} placeholder="Label"
                  className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none w-full" readOnly={!isIndependent} />
                <input value={it.to} onChange={(e) => { if (isIndependent) updateItem(idx, { to: e.target.value }); }} placeholder="/path"
                  className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none w-full font-mono" readOnly={!isIndependent} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => { if (isIndependent) moveUp(idx); }} disabled={idx===0 || !isIndependent}
                  className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => { if (isIndependent) moveDown(idx); }} disabled={idx===effectiveItems.length-1 || !isIndependent}
                  className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => { if (isIndependent) updateItem(idx, { visible: it.visible === false ? true : false }); }}
                  className="w-7 h-7 grid place-items-center rounded-md border border-gray-200">
                  {it.visible !== false ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                </button>
                {isIndependent && (
                  <button onClick={() => removeItem(idx)} className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 hover:bg-rose-50 hover:text-rose-600 text-gray-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {isIndependent && (
        <button onClick={addItem} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5]">
          <Plus className="w-4 h-4" /> Add mobile menu item
        </button>
      )}
    </Section>
  );
}

// ── Mobile Header Builder ─────────────────────────────────────────────────────
const MOBILE_SUBTABS = [
  { id: "layout",    label: "Layout" },
  { id: "logo",      label: "Logo" },
  { id: "name",      label: "Name" },
  { id: "nav",       label: "Nav" },
  { id: "buttons",   label: "Buttons" },
  { id: "hamburger", label: "Hamburger" },
  { id: "drawer",    label: "Drawer" },
];

const MOBILE_BPS = [480, 576, 640, 768, 820, 992, 1024];

// ── Tablet Tab — independent overrides for 768px–1023px ──────────────────────
function TabletTab({ d, patch }) {
  const set = (k, v) => patch({ [k]: v });
  const clr = (k) => patch({ [k]: null });
  return (
    <div className="space-y-5 py-2">
      <div className="text-xs text-gray-400 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
        These settings apply only at <strong>768px–1023px</strong> (tablet range).<br />
        Leave a field blank to inherit the desktop value.
      </div>

      <Section title="Visibility">
        <Row>
          <Field label="Logo visible">
            <select value={d.tablet_logo_visible === false ? "hide" : "show"}
              onChange={e => set("tablet_logo_visible", e.target.value !== "hide")} className={SI}>
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </Field>
          <Field label="Company name visible">
            <select value={d.tablet_company_name_visible === false ? "hide" : "show"}
              onChange={e => set("tablet_company_name_visible", e.target.value !== "hide")} className={SI}>
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </Field>
          <Field label="Navigation visible">
            <select value={d.tablet_nav_visible === false ? "hide" : "show"}
              onChange={e => set("tablet_nav_visible", e.target.value !== "hide")} className={SI}>
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </Field>
          <Field label="Book Now button">
            <select value={d.tablet_cta_btn_visible === false ? "hide" : "show"}
              onChange={e => set("tablet_cta_btn_visible", e.target.value !== "hide")} className={SI}>
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </Field>
          <Field label="Sign In button">
            <select value={d.tablet_signin_btn_visible === false ? "hide" : "show"}
              onChange={e => set("tablet_signin_btn_visible", e.target.value !== "hide")} className={SI}>
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </Field>
        </Row>
      </Section>

      <Section title="Header">
        <Row>
          <Field label="Header Height (px)">
            <NumInput value={d.tablet_header_height ?? ""} min={40} max={200} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_header_height", Number(e.target.value)) : clr("tablet_header_height")} />
          </Field>
          <Field label="Horizontal Padding (px)">
            <NumInput value={d.tablet_header_padding_x ?? ""} min={0} max={120} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_header_padding_x", Number(e.target.value)) : clr("tablet_header_padding_x")} />
          </Field>
          <Field label="Padding Top (px)">
            <NumInput value={d.tablet_header_padding_top ?? ""} min={0} max={80} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_header_padding_top", Number(e.target.value)) : clr("tablet_header_padding_top")} />
          </Field>
          <Field label="Padding Bottom (px)">
            <NumInput value={d.tablet_header_padding_bottom ?? ""} min={0} max={80} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_header_padding_bottom", Number(e.target.value)) : clr("tablet_header_padding_bottom")} />
          </Field>
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_header_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_header_margin_${side}`, Number(e.target.value)) : clr(`tablet_header_margin_${side}`)} />
            </Field>
          ))}
        </Row>
      </Section>

      <Section title="Logo">
        <Row>
          <Field label="Width (px)">
            <NumInput value={d.tablet_logo_width ?? ""} min={16} max={200} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_logo_width", Number(e.target.value)) : clr("tablet_logo_width")} />
          </Field>
          <Field label="Height (px)">
            <NumInput value={d.tablet_logo_height ?? ""} min={16} max={200} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_logo_height", Number(e.target.value)) : clr("tablet_logo_height")} />
          </Field>
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_logo_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_logo_margin_${side}`, Number(e.target.value)) : clr(`tablet_logo_margin_${side}`)} />
            </Field>
          ))}
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Padding (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Padding ${side}`}>
              <NumInput value={d[`tablet_logo_padding_${side}`] ?? ""} min={0} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_logo_padding_${side}`, Number(e.target.value)) : clr(`tablet_logo_padding_${side}`)} />
            </Field>
          ))}
        </Row>
      </Section>

      <Section title="Company Name">
        <Row>
          <Field label="Font Size (px)">
            <NumInput value={d.tablet_company_name_font_size ?? ""} min={8} max={72} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_company_name_font_size", Number(e.target.value)) : clr("tablet_company_name_font_size")} />
          </Field>
          <Field label="Max Width (px)">
            <NumInput value={d.tablet_company_name_max_width ?? ""} min={0} max={600} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_company_name_max_width", Number(e.target.value)) : clr("tablet_company_name_max_width")} />
          </Field>
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_company_name_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_company_name_margin_${side}`, Number(e.target.value)) : clr(`tablet_company_name_margin_${side}`)} />
            </Field>
          ))}
        </Row>
      </Section>

      <Section title="Navigation">
        <Row>
          <Field label="Font Size (px)">
            <NumInput value={d.tablet_nav_font_size ?? ""} min={8} max={32} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_nav_font_size", Number(e.target.value)) : clr("tablet_nav_font_size")} />
          </Field>
          <Field label="Gap between items (px)">
            <NumInput value={d.tablet_nav_gap ?? ""} min={0} max={80} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_nav_gap", Number(e.target.value)) : clr("tablet_nav_gap")} />
          </Field>
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_nav_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_nav_margin_${side}`, Number(e.target.value)) : clr(`tablet_nav_margin_${side}`)} />
            </Field>
          ))}
        </Row>
      </Section>

      <Section title="Buttons">
        <Row>
          <Field label="CTA Font Size (px)">
            <NumInput value={d.tablet_cta_btn_font_size ?? ""} min={8} max={24} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_cta_btn_font_size", Number(e.target.value)) : clr("tablet_cta_btn_font_size")} />
          </Field>
          <Field label="CTA Padding X (px)">
            <NumInput value={d.tablet_cta_btn_padding_x ?? ""} min={0} max={60} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_cta_btn_padding_x", Number(e.target.value)) : clr("tablet_cta_btn_padding_x")} />
          </Field>
          <Field label="CTA Padding Y (px)">
            <NumInput value={d.tablet_cta_btn_padding_y ?? ""} min={0} max={40} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_cta_btn_padding_y", Number(e.target.value)) : clr("tablet_cta_btn_padding_y")} />
          </Field>
          <Field label="Sign In Font Size (px)">
            <NumInput value={d.tablet_signin_btn_font_size ?? ""} min={8} max={24} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_signin_btn_font_size", Number(e.target.value)) : clr("tablet_signin_btn_font_size")} />
          </Field>
          <Field label="Sign In Padding X (px)">
            <NumInput value={d.tablet_signin_btn_padding_x ?? ""} min={0} max={60} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_signin_btn_padding_x", Number(e.target.value)) : clr("tablet_signin_btn_padding_x")} />
          </Field>
          <Field label="Sign In Padding Y (px)">
            <NumInput value={d.tablet_signin_btn_padding_y ?? ""} min={0} max={40} placeholder="inherit desktop"
              onChange={e => e.target.value ? set("tablet_signin_btn_padding_y", Number(e.target.value)) : clr("tablet_signin_btn_padding_y")} />
          </Field>
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">CTA margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_cta_btn_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_cta_btn_margin_${side}`, Number(e.target.value)) : clr(`tablet_cta_btn_margin_${side}`)} />
            </Field>
          ))}
        </Row>
        <p className="text-[11px] text-gray-400 mt-1">Sign In margin (px) — blank inherits desktop</p>
        <Row>
          {["top","right","bottom","left"].map((side) => (
            <Field key={side} label={`Margin ${side}`}>
              <NumInput value={d[`tablet_signin_btn_margin_${side}`] ?? ""} min={-40} max={80} placeholder="inherit"
                onChange={e => e.target.value ? set(`tablet_signin_btn_margin_${side}`, Number(e.target.value)) : clr(`tablet_signin_btn_margin_${side}`)} />
            </Field>
          ))}
        </Row>
      </Section>
    </div>
  );
}

function MobileTab({ d, set, setNum, patch, resetSection }) {
  const [sub, setSub] = useState("layout");
  const bp = d.mobile_breakpoint ?? 768;
  const mobileResetKeys = Object.keys(DEFAULTS).filter(k => k.startsWith("mobile_"));

  return (
    <div className="grid gap-5 max-w-2xl">
      {/* Enable toggle */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="text-sm font-semibold text-[#0F172A]">Enable Separate Mobile Header</div>
            <div className="text-xs text-gray-400 mt-0.5">When on, a fully independent mobile header replaces the desktop header below the breakpoint.</div>
          </div>
          <div onClick={() => set("mobile_separate", !d.mobile_separate)}
            className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 ${d.mobile_separate ? "bg-[#4F46E5]" : "bg-gray-200"}`}>
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${d.mobile_separate ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
        </label>
      </div>

      {/* Breakpoint */}
      <Section title="Breakpoint">
        <div className="grid grid-cols-3 gap-2 mb-2">
          {MOBILE_BPS.map((v) => (
            <button key={v} type="button" onClick={() => set("mobile_breakpoint", v)}
              className={`py-1.5 text-xs rounded-lg border font-medium transition-colors ${bp === v ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
              {v}px
            </button>
          ))}
        </div>
        <NumInput value={bp} min={320} max={1400}
          onChange={(e) => set("mobile_breakpoint", Number(e.target.value))} placeholder="Custom value" />
      </Section>

      {/* Sub-tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {MOBILE_SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`flex-1 min-w-[60px] py-1.5 px-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${sub === t.id ? "bg-white shadow text-[#0F172A]" : "text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Layout */}
      {sub === "layout" && (
        <div className="grid gap-4">
          <Section title="Header dimensions">
            <Row cols={2}>
              <Field label="Height (px)"><NumInput value={d.mobile_header_height ?? 56} min={40} max={160} onChange={(e) => set("mobile_header_height", Number(e.target.value))} /></Field>
              <Field label="Max width (px)"><NumInput value={d.mobile_header_max_width ?? ""} placeholder="Full width" onChange={(e) => set("mobile_header_max_width", e.target.value ? Number(e.target.value) : null)} /></Field>
            </Row>
          </Section>
          <Section title="Background">
            <Row cols={2}>
              <ColorField label="Background" value={(d.mobile_header_bg || "#FFFFFF").replace(/rgba?\([^)]+\)/, "#FFFFFF")} onChange={(e) => set("mobile_header_bg", e.target.value)} />
              <Field label="Gradient CSS"><TextInput value={d.mobile_header_gradient || ""} placeholder="linear-gradient(…)" onChange={(e) => set("mobile_header_gradient", e.target.value)} /></Field>
            </Row>
            <div className="flex gap-4">
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.mobile_header_blur} onChange={(e) => set("mobile_header_blur", e.target.checked)} />Backdrop blur</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_header_shadow !== false} onChange={(e) => set("mobile_header_shadow", e.target.checked)} />Bottom shadow</label>
            </div>
          </Section>
          <Section title="Border">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.mobile_header_border} onChange={(e) => set("mobile_header_border", e.target.checked)} />Show bottom border</label>
            <Row cols={2}>
              <ColorField label="Border color" value={d.mobile_header_border_color || "#E2E8F0"} onChange={(e) => set("mobile_header_border_color", e.target.value)} />
              <Field label="Border radius (px)"><NumInput value={d.mobile_header_border_radius ?? 0} min={0} max={40} onChange={(e) => set("mobile_header_border_radius", Number(e.target.value))} /></Field>
            </Row>
          </Section>
          <Section title="Position &amp; visibility">
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_header_sticky !== false} onChange={(e) => set("mobile_header_sticky", e.target.checked)} />Sticky on scroll</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.mobile_header_fixed} onChange={(e) => set("mobile_header_fixed", e.target.checked)} />Fixed position</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.mobile_header_transparent} onChange={(e) => set("mobile_header_transparent", e.target.checked)} />Transparent background</label>
            </div>
            <Row cols={2}>
              <Field label="Z-index"><NumInput value={d.mobile_header_zindex ?? 40} min={0} max={9999} onChange={(e) => set("mobile_header_zindex", Number(e.target.value))} /></Field>
              <Field label="Opacity (0–1)"><NumInput value={d.mobile_header_opacity ?? 1} step={0.05} min={0} max={1} onChange={(e) => set("mobile_header_opacity", Number(e.target.value))} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Header width CSS"><TextInput value={d.mobile_header_width || ""} placeholder="100%" onChange={(e) => set("mobile_header_width", e.target.value)} /></Field>
              <Field label="Container max-width CSS"><TextInput value={d.mobile_container_width || ""} placeholder="100%" onChange={(e) => set("mobile_container_width", e.target.value)} /></Field>
            </Row>
          </Section>
          <Section title="Padding">
            <div className="grid grid-cols-2 gap-3">
              {[["Top","mobile_header_padding_top",10],["Bottom","mobile_header_padding_bottom",10],["Left","mobile_header_padding_left",16],["Right","mobile_header_padding_right",16]].map(([lbl,k,def]) => (
                <Field key={k} label={lbl}><NumInput value={d[k] ?? def} min={0} max={60} onChange={(e) => set(k, Number(e.target.value))} /></Field>
              ))}
            </div>
          </Section>
        </div>
      )}

      {/* Logo */}
      {sub === "logo" && (
        <div className="grid gap-4">
          <Section title="Mobile logo upload">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_logo_visible !== false} onChange={(e) => set("mobile_logo_visible", e.target.checked)} />Show logo</label>
            <ImageUpload value={d.mobile_logo_url} onChange={(v) => set("mobile_logo_url", v)} testid="mobile-logo-upload" accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp" />
            {d.mobile_logo_url && (<button onClick={() => set("mobile_logo_url", "")} className="text-xs text-rose-600 hover:underline">Remove</button>)}
            <p className="text-xs text-gray-400">Upload an independent mobile logo. When not uploaded, no logo will appear in the mobile header (desktop logo is never used).</p>
          </Section>
          <Section title="Size &amp; shape">
            <SliderInput label="Width (px)" value={d.mobile_logo_width ?? 32} min={8} max={200} onChange={(e) => set("mobile_logo_width", Number(e.target.value))} />
            <SliderInput label="Height (px)" value={d.mobile_logo_height ?? 32} min={8} max={200} onChange={(e) => set("mobile_logo_height", Number(e.target.value))} />
            <SliderInput label="Border radius (px)" value={d.mobile_logo_border_radius ?? 8} min={0} max={100} onChange={(e) => set("mobile_logo_border_radius", Number(e.target.value))} />
            <Row cols={2}>
              <Field label="Max width (px)"><NumInput value={d.mobile_logo_max_width ?? ""} placeholder="auto" onChange={(e) => set("mobile_logo_max_width", e.target.value ? Number(e.target.value) : null)} /></Field>
              <Field label="Max height (px)"><NumInput value={d.mobile_logo_max_height ?? ""} placeholder="auto" onChange={(e) => set("mobile_logo_max_height", e.target.value ? Number(e.target.value) : null)} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Min width (px)"><NumInput value={d.mobile_logo_min_width ?? ""} placeholder="none" onChange={(e) => set("mobile_logo_min_width", e.target.value ? Number(e.target.value) : null)} /></Field>
              <Field label="Min height (px)"><NumInput value={d.mobile_logo_min_height ?? ""} placeholder="none" onChange={(e) => set("mobile_logo_min_height", e.target.value ? Number(e.target.value) : null)} /></Field>
            </Row>
            <SliderInput label="Scale" value={Math.round((d.mobile_logo_scale ?? 1) * 100)} min={10} max={300} unit="%"
              onChange={(e) => set("mobile_logo_scale", Number(e.target.value) / 100)} />
            <Row cols={2}>
              <Field label="Object fit"><Select value={d.mobile_logo_object_fit || "contain"} onChange={(e) => set("mobile_logo_object_fit", e.target.value)}><option value="contain">Contain</option><option value="cover">Cover</option><option value="fill">Fill</option></Select></Field>
              <Field label="Z-index"><NumInput value={d.mobile_logo_zindex ?? ""} placeholder="auto" onChange={(e) => set("mobile_logo_zindex", e.target.value ? Number(e.target.value) : null)} /></Field>
            </Row>
          </Section>
          <Section title="Appearance">
            <Row cols={2}>
              <ColorField label="Background" value={d.mobile_logo_bg || "#FFFFFF"} onChange={(e) => set("mobile_logo_bg", e.target.value)} />
              <Field label="Opacity (0–1)"><NumInput value={d.mobile_logo_opacity ?? 1} step={0.05} min={0} max={1} onChange={(e) => set("mobile_logo_opacity", Number(e.target.value))} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Rotation (deg)"><NumInput value={d.mobile_logo_rotation ?? 0} min={-180} max={180} onChange={(e) => set("mobile_logo_rotation", Number(e.target.value))} /></Field>
              <Field label="Shadow CSS"><TextInput value={d.mobile_logo_shadow || ""} placeholder="0 2px 8px rgba(0,0,0,.15)" onChange={(e) => set("mobile_logo_shadow", e.target.value)} /></Field>
            </Row>
            <Field label="Alignment"><Select value={d.mobile_logo_align || "left"} onChange={(e) => set("mobile_logo_align", e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></Select></Field>
            <Row cols={2}>
              <Field label="Offset X (px)"><NumInput value={d.mobile_logo_offset_x ?? 0} min={-100} max={100} onChange={(e) => set("mobile_logo_offset_x", Number(e.target.value))} /></Field>
              <Field label="Offset Y (px)"><NumInput value={d.mobile_logo_offset_y ?? 0} min={-100} max={100} onChange={(e) => set("mobile_logo_offset_y", Number(e.target.value))} /></Field>
            </Row>
          </Section>
          <Section title="Spacing">
            <SpacingBox label="Margin" prefix="mobile_logo_margin" d={d} patch={patch} />
            <SpacingBox label="Padding" prefix="mobile_logo_padding" d={d} patch={patch} />
          </Section>
        </div>
      )}

      {/* Name */}
      {sub === "name" && (
        <div className="grid gap-4">
          <Section title="Mobile company name">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_company_name_visible !== false} onChange={(e) => set("mobile_company_name_visible", e.target.checked)} />Show company name</label>
            <Field label="Mobile name (blank = use desktop name)"><TextInput value={d.mobile_company_name || ""} placeholder={d.company_name || "Company name"} onChange={(e) => set("mobile_company_name", e.target.value)} /></Field>
            <div className="px-3 py-3 rounded-xl bg-gray-50 text-center" style={{ fontFamily: fontCss(d.mobile_name_font_family || "Inter"), fontSize: Math.min(d.mobile_name_font_size ?? 16, 22), fontWeight: d.mobile_name_font_weight ?? 600, color: d.mobile_name_color || "#0F172A" }}>
              {d.mobile_company_name || d.company_name || "Company Name"}
            </div>
          </Section>
          <Section title="Typography">
            <Field label="Font family"><Select value={d.mobile_name_font_family || "Inter"} onChange={(e) => set("mobile_name_font_family", e.target.value)}>{FONT_GROUPS.map((g) => (<optgroup key={g.label} label={g.label}>{g.fonts.map((f) => <option key={f} value={f}>{f}</option>)}</optgroup>))}</Select></Field>
            <Row cols={2}>
              <Field label="Font size (px)"><NumInput value={d.mobile_name_font_size ?? 16} min={8} max={48} onChange={(e) => set("mobile_name_font_size", Number(e.target.value))} /></Field>
              <Field label="Font weight"><Select value={d.mobile_name_font_weight ?? 600} onChange={(e) => set("mobile_name_font_weight", Number(e.target.value))}>{[300,400,500,600,700,800].map(w => <option key={w} value={w}>{w}</option>)}</Select></Field>
            </Row>
            <Row cols={2}>
              <Field label="Style (Italic)"><Select value={d.mobile_name_font_style || "normal"} onChange={(e) => set("mobile_name_font_style", e.target.value)}><option value="normal">Normal</option><option value="italic">Italic</option></Select></Field>
              <Field label="Underline"><Select value={d.mobile_name_text_decoration || "none"} onChange={(e) => set("mobile_name_text_decoration", e.target.value)}><option value="none">None</option><option value="underline">Underline</option></Select></Field>
            </Row>
            <ColorField label="Color" value={d.mobile_name_color || "#0F172A"} onChange={(e) => set("mobile_name_color", e.target.value)} />
            <Row cols={2}>
              <Field label="Letter spacing (em)"><NumInput value={d.mobile_name_letter_spacing ?? -0.01} step={0.01} min={-0.2} max={0.5} onChange={(e) => set("mobile_name_letter_spacing", Number(e.target.value))} /></Field>
              <Field label="Line height"><NumInput value={d.mobile_name_line_height ?? 1.2} step={0.05} min={1} max={3} onChange={(e) => set("mobile_name_line_height", Number(e.target.value))} /></Field>
            </Row>
            <Field label="Align"><Select value={d.mobile_name_align || "left"} onChange={(e) => set("mobile_name_align", e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></Select></Field>
            <Row cols={2}>
              <Field label="Text transform"><Select value={d.mobile_name_text_transform || "none"} onChange={(e) => set("mobile_name_text_transform", e.target.value)}><option value="none">None</option><option value="uppercase">UPPER</option><option value="lowercase">lower</option><option value="capitalize">Capitalize</option></Select></Field>
              <Field label="Text shadow CSS"><TextInput value={d.mobile_name_text_shadow || ""} placeholder="1px 1px 2px rgba(0,0,0,.2)" onChange={(e) => set("mobile_name_text_shadow", e.target.value)} /></Field>
            </Row>
          </Section>
          <Section title="Short name &amp; tagline">
            <Field label="Short name (very small screens, blank = use main name)"><TextInput value={d.mobile_name_short || ""} placeholder="e.g. EL" onChange={(e) => set("mobile_name_short", e.target.value)} /></Field>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.mobile_tagline_visible} onChange={(e) => set("mobile_tagline_visible", e.target.checked)} />Show tagline below name</label>
            <Field label="Tagline text"><TextInput value={d.mobile_tagline || ""} placeholder="e.g. Teacher Training Institute" onChange={(e) => set("mobile_tagline", e.target.value)} /></Field>
          </Section>
          <Section title="Spacing"><SpacingBox label="Margin" prefix="mobile_name_margin" d={d} patch={patch} /><SpacingBox label="Padding" prefix="mobile_name_padding" d={d} patch={patch} /></Section>
        </div>
      )}

      {/* Nav */}
      {sub === "nav" && (
        <div className="grid gap-4">
          <Section title="Mobile nav typography">
            <Field label="Font family"><Select value={d.mobile_nav_font_family || "Inter"} onChange={(e) => set("mobile_nav_font_family", e.target.value)}>{FONT_GROUPS.map((g) => (<optgroup key={g.label} label={g.label}>{g.fonts.map((f) => <option key={f} value={f}>{f}</option>)}</optgroup>))}</Select></Field>
            <Row cols={2}>
              <Field label="Font size (px)"><NumInput value={d.mobile_nav_font_size ?? 15} min={10} max={24} onChange={(e) => set("mobile_nav_font_size", Number(e.target.value))} /></Field>
              <Field label="Font weight"><Select value={d.mobile_nav_font_weight ?? 500} onChange={(e) => set("mobile_nav_font_weight", Number(e.target.value))}>{[400,500,600,700].map(w => <option key={w} value={w}>{w}</option>)}</Select></Field>
            </Row>
            <Row cols={2}>
              <Field label="Style (Italic)"><Select value={d.mobile_nav_font_style || "normal"} onChange={(e) => set("mobile_nav_font_style", e.target.value)}><option value="normal">Normal</option><option value="italic">Italic</option></Select></Field>
              <Field label="Underline"><Select value={d.mobile_nav_text_decoration || "none"} onChange={(e) => set("mobile_nav_text_decoration", e.target.value)}><option value="none">None</option><option value="underline">Underline</option></Select></Field>
            </Row>
            <ColorField label="Text color" value={d.mobile_nav_color || "#0F172A"} onChange={(e) => set("mobile_nav_color", e.target.value)} />
          </Section>
          <Section title="Item spacing">
            <div className="grid grid-cols-2 gap-3">
              {[["Top","mobile_nav_item_padding_top",12],["Bottom","mobile_nav_item_padding_bottom",12],["Left","mobile_nav_item_padding_left",12],["Right","mobile_nav_item_padding_right",12]].map(([lbl,k,def]) => (
                <Field key={k} label={`Padding ${lbl}`}><NumInput value={d[k] ?? def} min={0} max={40} onChange={(e) => set(k, Number(e.target.value))} /></Field>
              ))}
            </div>
            <Field label="Item border radius (px)"><NumInput value={d.mobile_nav_item_border_radius ?? 12} min={0} max={50} onChange={(e) => set("mobile_nav_item_border_radius", Number(e.target.value))} /></Field>
          </Section>
          <Section title="Mobile nav colors &amp; spacing">
            <Row cols={3}>
              <ColorField label="Text color" value={d.mobile_nav_color || "#0F172A"} onChange={(e) => set("mobile_nav_color", e.target.value)} />
              <ColorField label="Hover color" value={d.mobile_nav_hover_color || "#4F46E5"} onChange={(e) => set("mobile_nav_hover_color", e.target.value)} />
              <ColorField label="Active color" value={d.mobile_nav_active_color || "#4F46E5"} onChange={(e) => set("mobile_nav_active_color", e.target.value)} />
            </Row>
            <Field label="Item gap (px)"><NumInput value={d.mobile_nav_gap ?? 4} min={0} max={24} onChange={(e) => set("mobile_nav_gap", Number(e.target.value))} /></Field>
          </Section>
          <MobileNavItemsEditor d={d} patch={patch} />
        </div>
      )}

      {/* Buttons */}
      {sub === "buttons" && (
        <div className="grid gap-4">
          <Section title="CTA button (in header bar)">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_show_cta !== false} onChange={(e) => set("mobile_show_cta", e.target.checked)} />Show CTA in mobile header</label>
            <Field label="Button text (blank = desktop text)"><TextInput value={d.mobile_cta_text || ""} placeholder={d.cta_text || "Book a Training"} onChange={(e) => set("mobile_cta_text", e.target.value)} /></Field>
            <Row cols={2}>
              <ColorField label="Background" value={d.mobile_cta_bg || "#0F172A"} onChange={(e) => set("mobile_cta_bg", e.target.value)} />
              <ColorField label="Text color" value={d.mobile_cta_color || "#FFFFFF"} onChange={(e) => set("mobile_cta_color", e.target.value)} />
            </Row>
            <Row cols={2}>
              <Field label="Font size (px)"><NumInput value={d.mobile_cta_font_size ?? 13} min={10} max={24} onChange={(e) => set("mobile_cta_font_size", Number(e.target.value))} /></Field>
              <Field label="Font weight"><Select value={d.mobile_cta_font_weight ?? 600} onChange={(e) => set("mobile_cta_font_weight", Number(e.target.value))}>{[400,500,600,700].map(w => <option key={w} value={w}>{w}</option>)}</Select></Field>
            </Row>
            <Row cols={2}>
              <Field label="Border radius (px)"><NumInput value={d.mobile_cta_border_radius ?? 8} min={0} max={50} onChange={(e) => set("mobile_cta_border_radius", Number(e.target.value))} /></Field>
              <Field label="H padding (px)"><NumInput value={d.mobile_cta_padding_x ?? 14} min={0} max={60} onChange={(e) => set("mobile_cta_padding_x", Number(e.target.value))} /></Field>
            </Row>
            <Field label="V padding (px)"><NumInput value={d.mobile_cta_padding_y ?? 8} min={0} max={40} onChange={(e) => set("mobile_cta_padding_y", Number(e.target.value))} /></Field>
            <Row cols={2}>
              <Field label="Width CSS"><TextInput value={d.mobile_cta_width || ""} placeholder="auto" onChange={(e) => set("mobile_cta_width", e.target.value)} /></Field>
              <Field label="Height CSS"><TextInput value={d.mobile_cta_height || ""} placeholder="auto" onChange={(e) => set("mobile_cta_height", e.target.value)} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Border"><Select value={d.mobile_cta_border || "none"} onChange={(e) => set("mobile_cta_border", e.target.value)}><option value="none">None</option><option value="solid">Solid</option><option value="dashed">Dashed</option></Select></Field>
              {d.mobile_cta_border && d.mobile_cta_border !== "none" && <ColorField label="Border color" value={d.mobile_cta_border_color || "#E2E8F0"} onChange={(e) => set("mobile_cta_border_color", e.target.value)} />}
            </Row>
            <Field label="Alignment"><Select value={d.mobile_cta_align || "center"} onChange={(e) => set("mobile_cta_align", e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></Select></Field>
            <SpacingBox label="CTA Margin" prefix="mobile_cta_margin" d={d} patch={patch} />
          </Section>
          <Section title="Sign In (in drawer)">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_show_signin !== false} onChange={(e) => set("mobile_show_signin", e.target.checked)} />Show Sign In in mobile menu</label>
            <Field label="Text (blank = desktop text)"><TextInput value={d.mobile_signin_text || ""} placeholder={d.signin_btn_text || "Sign in"} onChange={(e) => set("mobile_signin_text", e.target.value)} /></Field>
            <Row cols={2}>
              <ColorField label="Text color" value={d.mobile_signin_color || "#374151"} onChange={(e) => set("mobile_signin_color", e.target.value)} />
              <Field label="Font size (px)"><NumInput value={d.mobile_signin_font_size ?? 14} min={10} max={24} onChange={(e) => set("mobile_signin_font_size", Number(e.target.value))} /></Field>
            </Row>
            <Field label="Font weight"><Select value={d.mobile_signin_font_weight ?? 500} onChange={(e) => set("mobile_signin_font_weight", Number(e.target.value))}>{[400,500,600,700].map(w => <option key={w} value={w}>{w}</option>)}</Select></Field>
            <Row cols={2}>
              <ColorField label="Background" value={d.mobile_signin_bg && d.mobile_signin_bg !== "transparent" ? d.mobile_signin_bg : "#ffffff"} onChange={(e) => set("mobile_signin_bg", e.target.value)} />
              <Field label="Border"><Select value={d.mobile_signin_border || "none"} onChange={(e) => set("mobile_signin_border", e.target.value)}><option value="none">None</option><option value="solid">Solid</option><option value="dashed">Dashed</option></Select></Field>
            </Row>
            {d.mobile_signin_border && d.mobile_signin_border !== "none" && (
              <Row cols={2}>
                <ColorField label="Border color" value={d.mobile_signin_border_color || "#E2E8F0"} onChange={(e) => set("mobile_signin_border_color", e.target.value)} />
                <Field label="Border radius (px)"><NumInput value={d.mobile_signin_border_radius ?? 8} min={0} max={50} onChange={(e) => set("mobile_signin_border_radius", Number(e.target.value))} /></Field>
              </Row>
            )}
            <Row cols={2}>
              <Field label="H padding (px)"><NumInput value={d.mobile_signin_padding_x ?? 12} min={0} max={40} onChange={(e) => set("mobile_signin_padding_x", Number(e.target.value))} /></Field>
              <Field label="V padding (px)"><NumInput value={d.mobile_signin_padding_y ?? 8} min={0} max={40} onChange={(e) => set("mobile_signin_padding_y", Number(e.target.value))} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Width CSS"><TextInput value={d.mobile_signin_width || ""} placeholder="auto" onChange={(e) => set("mobile_signin_width", e.target.value)} /></Field>
              <Field label="Height CSS"><TextInput value={d.mobile_signin_height || ""} placeholder="auto" onChange={(e) => set("mobile_signin_height", e.target.value)} /></Field>
            </Row>
            <Field label="Alignment"><Select value={d.mobile_signin_align || "left"} onChange={(e) => set("mobile_signin_align", e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></Select></Field>
          </Section>
        </div>
      )}

      {/* Hamburger */}
      {sub === "hamburger" && (
        <div className="grid gap-4">
          <Section title="Hamburger icon">
            <Row cols={2}>
              <ColorField label="Bar color" value={d.mobile_hamburger_color || "#374151"} onChange={(e) => set("mobile_hamburger_color", e.target.value)} />
              <ColorField label="Button background" value={d.mobile_hamburger_bg || "#FFFFFF"} onChange={(e) => set("mobile_hamburger_bg", e.target.value)} />
            </Row>
            <Row cols={2}>
              <Field label="Icon size (px)"><NumInput value={d.mobile_hamburger_size ?? 20} min={12} max={40} onChange={(e) => set("mobile_hamburger_size", Number(e.target.value))} /></Field>
              <Field label="Bar thickness (px)"><NumInput value={d.mobile_hamburger_thickness ?? 2} min={1} max={6} onChange={(e) => set("mobile_hamburger_thickness", Number(e.target.value))} /></Field>
            </Row>
            <Row cols={2}>
              <Field label="Button border radius (px)"><NumInput value={d.mobile_hamburger_border_radius ?? 8} min={0} max={50} onChange={(e) => set("mobile_hamburger_border_radius", Number(e.target.value))} /></Field>
              <Field label="Button padding (px)"><NumInput value={d.mobile_hamburger_padding ?? 8} min={0} max={24} onChange={(e) => set("mobile_hamburger_padding", Number(e.target.value))} /></Field>
            </Row>
            <Field label="Position"><Select value={d.mobile_hamburger_position || "right"} onChange={(e) => set("mobile_hamburger_position", e.target.value)}><option value="right">Right</option><option value="left">Left</option></Select></Field>
          </Section>
          <Section title="Hover &amp; animation">
            <Row cols={2}>
              <ColorField label="Hover color" value={d.mobile_hamburger_hover_color || "#0F172A"} onChange={(e) => set("mobile_hamburger_hover_color", e.target.value)} />
              <Field label="Animation"><Select value={d.mobile_hamburger_animation || "none"} onChange={(e) => set("mobile_hamburger_animation", e.target.value)}><option value="none">None</option><option value="spin">Spin to X</option><option value="fade">Fade</option></Select></Field>
            </Row>
          </Section>
          <SpacingBox label="Hamburger Margin" prefix="mobile_hamburger_margin" d={d} patch={patch} />
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 flex items-center justify-center gap-3">
            <span className="text-xs text-gray-400">Preview:</span>
            <div style={{ padding: d.mobile_hamburger_padding ?? 8, background: d.mobile_hamburger_bg || "transparent", borderRadius: d.mobile_hamburger_border_radius ?? 8 }}>
              {[0,1,2].map((i) => (<div key={i} className={i < 2 ? "mb-1" : ""} style={{ width: d.mobile_hamburger_size ?? 20, height: d.mobile_hamburger_thickness ?? 2, background: d.mobile_hamburger_color || "#374151", borderRadius: 2 }} />))}
            </div>
          </div>
        </div>
      )}

      {/* Drawer */}
      {sub === "drawer" && (
        <div className="grid gap-4">
          <Section title="Drawer appearance">
            <Row cols={2}>
              <ColorField label="Background" value={d.mobile_menu_bg || "#FFFFFF"} onChange={(e) => set("mobile_menu_bg", e.target.value)} />
              <Field label="Position"><Select value={d.mobile_menu_position || "left"} onChange={(e) => set("mobile_menu_position", e.target.value)}><option value="left">From left</option><option value="right">From right</option><option value="top">From top</option></Select></Field>
            </Row>
            <Row cols={2}>
              <Field label="Width (px)"><NumInput value={d.mobile_menu_width ?? 280} min={160} max={600} onChange={(e) => set("mobile_menu_width", Number(e.target.value))} /></Field>
              <Field label="Border radius (px)"><NumInput value={d.mobile_menu_border_radius ?? 0} min={0} max={32} onChange={(e) => set("mobile_menu_border_radius", Number(e.target.value))} /></Field>
            </Row>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_menu_shadow !== false} onChange={(e) => set("mobile_menu_shadow", e.target.checked)} />Drop shadow</label>
          </Section>
          <Section title="Animation">
            <Row cols={2}>
              <Field label="Style"><Select value={d.mobile_menu_animation || "slide"} onChange={(e) => set("mobile_menu_animation", e.target.value)}><option value="slide">Slide</option><option value="fade">Fade</option><option value="none">None</option></Select></Field>
              <Field label="Speed (ms)"><NumInput value={d.mobile_menu_animation_speed ?? 250} min={50} max={800} onChange={(e) => set("mobile_menu_animation_speed", Number(e.target.value))} /></Field>
            </Row>
          </Section>
          <Section title="Overlay">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_menu_overlay !== false} onChange={(e) => set("mobile_menu_overlay", e.target.checked)} />Show overlay</label>
            <Row cols={2}>
              <ColorField label="Overlay color" value={d.mobile_overlay_color || "#000000"} onChange={(e) => set("mobile_overlay_color", e.target.value)} />
              <Field label="Opacity (0–1)"><NumInput value={d.mobile_overlay_opacity ?? 0.3} step={0.05} min={0} max={1} onChange={(e) => set("mobile_overlay_opacity", Number(e.target.value))} /></Field>
            </Row>
          </Section>
          <Section title="Close button">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.mobile_drawer_close_btn !== false} onChange={(e) => set("mobile_drawer_close_btn", e.target.checked)} />Show close (X) button at top of drawer</label>
            <Field label="Close icon size (px)"><NumInput value={d.mobile_drawer_close_size ?? 20} min={12} max={40} onChange={(e) => set("mobile_drawer_close_size", Number(e.target.value))} /></Field>
          </Section>
        </div>
      )}

      <button onClick={() => resetSection(mobileResetKeys)}
        className="inline-flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-medium border border-rose-200 bg-rose-50 px-3 py-2 rounded-lg w-fit">
        <RotateCcw className="w-3.5 h-3.5" /> Reset all mobile settings
      </button>
    </div>
  );
}
